// src/shared/components/MarkdownField/MarkdownToolbar.tsx

import { Group, SegmentedControl, ActionIcon, Tooltip } from '@mantine/core';
import { IconBold, IconItalic, IconLink, IconList } from '@tabler/icons-react';
import { MarkdownHelpPopover } from './MarkdownHelpPopover';
import { MarkdownInsert, type InsertResult } from '@/shared/utils/MarkdownInsert';
import type { RefObject } from 'react';

type Mode = 'write' | 'preview';

interface MarkdownToolbarProps {
  mode: Mode;
  onModeChange: (mode: Mode) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onInsert: (result: InsertResult) => void;
}

/**
 * Toolbar for MarkdownField with Write/Preview toggle and formatting buttons.
 */
export function MarkdownToolbar({
  mode,
  onModeChange,
  textareaRef,
  value,
  onInsert,
}: MarkdownToolbarProps) {
  const isPreview = mode === 'preview';

  const getSelection = () => {
    const textarea = textareaRef.current;
    if (!textarea) return { start: 0, end: 0 };
    return { start: textarea.selectionStart, end: textarea.selectionEnd };
  };

  const handleBold = () => {
    const { start, end } = getSelection();
    onInsert(MarkdownInsert.bold(value, start, end));
  };

  const handleItalic = () => {
    const { start, end } = getSelection();
    onInsert(MarkdownInsert.italic(value, start, end));
  };

  const handleLink = () => {
    const { start, end } = getSelection();
    onInsert(MarkdownInsert.insertLink(value, start, end));
  };

  const handleList = () => {
    const { start, end } = getSelection();
    onInsert(MarkdownInsert.bulletList(value, start, end));
  };

  return (
    <Group justify="space-between" gap="xs" mb="xs">
      <Group gap="xs">
        <SegmentedControl
          size="xs"
          value={mode}
          onChange={(val) => onModeChange(val as Mode)}
          data={[
            { label: 'Write', value: 'write' },
            { label: 'Preview', value: 'preview' },
          ]}
        />

        <Group gap={4}>
          <Tooltip label="Bold" position="bottom" openDelay={400}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={handleBold}
              disabled={isPreview}
              aria-label="Bold"
            >
              <IconBold size={16} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Italic" position="bottom" openDelay={400}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={handleItalic}
              disabled={isPreview}
              aria-label="Italic"
            >
              <IconItalic size={16} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Link" position="bottom" openDelay={400}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={handleLink}
              disabled={isPreview}
              aria-label="Link"
            >
              <IconLink size={16} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Bullet list" position="bottom" openDelay={400}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={handleList}
              disabled={isPreview}
              aria-label="Bullet list"
            >
              <IconList size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <MarkdownHelpPopover />
    </Group>
  );
}
